/**
 * Right-side sidebar that opens after the user picks a concept from
 * the v2 Landing search dropdown.
 *
 * Shows three states based on the GFC's NDC universe:
 *
 *   1. The GFC has at least one active real or temp NDC -> render the
 *      list with checkboxes; user picks one or more and clicks
 *      Continue. We navigate to /fdp/provisonal_forecast/edit with
 *      gfc + the chosen NDC values in the query string.
 *
 *   2. The GFC has zero NDCs -> render the inline "Create Temporary
 *      Packout NDC" panel with PackoutMAS / PackoutNC cards. On
 *      success the new row is pushed back into the sidebar list and
 *      auto-selected, so the user can immediately Continue.
 *
 *   3. Loading -> shows a lightweight loading message; we don't block
 *      the sidebar from opening because PrimeReact's Sidebar slide-in
 *      animation runs concurrently with the API fetch.
 */

import { useState } from 'react';
import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Skeleton } from 'primereact/skeleton';

import { useStore } from 'hooks/useStore';
import { TemporaryPackoutNDCType } from 'Interfaces/FDPInterfaces/provisionalForecastV2Interfaces';
import CopyButton from './CopyButton';
import RightPanel from './RightPanel';

const ConceptDetailSidebar = observer(() => {
  const navigate = useNavigate();
  const {
    ProvisionalForecastV2Store: {
      isSidebarOpen,
      selectedConcept,
      activeNDCs,
      temporaryNDCs,
      conceptType,
      isLoadingNDCs,
      selectedNDCsForEdit,
      isCreatingTempNDC,
      lastCreatedTempNDC,
      closeSidebar,
      toggleNDCSelection,
      createTempNDC,
      dismissLastCreatedTempNDC,
    },
  } = useStore();

  // Local UI choice — which Type the user clicked in the create
  // panel before they hit "Create". We only POST after they confirm.
  const [pendingTempType, setPendingTempType] =
    useState<TemporaryPackoutNDCType | null>(null);

  if (!selectedConcept) return null;

  const hasAnyNDCs = activeNDCs.length > 0 || temporaryNDCs.length > 0;
  const canContinue = selectedNDCsForEdit.length > 0;

  const handleContinue = () => {
    if (!canContinue || !selectedConcept) return;
    const params = new URLSearchParams({
      gfc: String(selectedConcept.GFC),
      ndcs: selectedNDCsForEdit.join(','),
    });
    closeSidebar();
    navigate(`/fdp/provisonal_forecast/edit?${params.toString()}`);
  };

  const handleCreateTempNDC = async () => {
    if (!pendingTempType || !selectedConcept) return;
    const created = await createTempNDC(selectedConcept.GFC, pendingTempType);
    if (created) {
      // Clear the local UI choice; the store has pushed the row into
      // temporaryNDCs and auto-selected it for the user.
      setPendingTempType(null);
    }
  };

  return (
    <RightPanel
      visible={isSidebarOpen}
      onHide={() => {
        closeSidebar();
        setPendingTempType(null);
      }}
      header={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {/* Concept name row — copy icon sits inline on the right
              edge of the title so it reads as part of the same
              field, not a stray control. */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              minWidth: 0,
            }}
          >
            <span
              style={{
                fontSize: 16,
                fontWeight: 600,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={selectedConcept.Concept}
            >
              {selectedConcept.Concept}
            </span>
            <CopyButton
              value={selectedConcept.Concept}
              ariaLabel="Copy concept name"
            />
          </div>

          {/* GFC row — same inline-copy pattern. Concept-level
              contract-type chip sits on the same line so the header
              stays compact; rendered only when the backend resolved
              a non-null bucket. */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <span style={{ fontSize: 12, color: '#6b7280' }}>
              GFC {selectedConcept.GFC}
            </span>
            <CopyButton value={selectedConcept.GFC} ariaLabel="Copy GFC" />
            {conceptType && (
              <span
                aria-label={`Contract type ${conceptType}`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '1px 8px',
                  fontSize: 10.5,
                  fontWeight: 600,
                  letterSpacing: 0.4,
                  color: '#6b7280',
                  border: '1px solid #d1d5db',
                  borderRadius: 999,
                  background: 'transparent',
                  textTransform: 'uppercase',
                  lineHeight: 1.5,
                }}
              >
                {conceptType}
              </span>
            )}
          </div>
        </div>
      }
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          height: '100%',
        }}
      >
        {isLoadingNDCs && <NDCListSkeleton />}

        {!isLoadingNDCs && lastCreatedTempNDC && (
          <div
            role="status"
            aria-live="polite"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              padding: '0.65rem 0.8rem',
              borderRadius: 8,
              background: '#ecfdf5',
              border: '1px solid #a7f3d0',
              color: '#065f46',
              fontSize: 12.5,
              lineHeight: 1.45,
            }}
          >
            <span style={{ fontSize: 14, lineHeight: 1 }} aria-hidden>
              ✓
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <strong style={{ display: 'block', marginBottom: 2 }}>
                Temporary NDC created
              </strong>
              <span style={{ wordBreak: 'break-all' }}>
                {lastCreatedTempNDC} is now selected below — click Continue
                to begin forecasting.
              </span>
            </div>
            <button
              type="button"
              onClick={dismissLastCreatedTempNDC}
              aria-label="Dismiss"
              style={{
                background: 'transparent',
                border: 'none',
                color: '#065f46',
                cursor: 'pointer',
                fontSize: 14,
                lineHeight: 1,
                padding: 2,
              }}
            >
              ×
            </button>
          </div>
        )}

        {!isLoadingNDCs && hasAnyNDCs && (
          <>
            <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>
              Select one or more NDCs to edit the Provisional Forecast.
            </p>

            {activeNDCs.length > 0 && (
              <div>
                <h4
                  style={{
                    margin: '0 0 8px',
                    fontSize: 12,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    color: '#374151',
                    letterSpacing: 0.4,
                  }}
                >
                  Active NDCs
                </h4>
                <div
                  style={{
                    border: '1px solid var(--border-primary)',
                    borderRadius: 8,
                    background: 'var(--bg-card)',
                  }}
                >
                  {activeNDCs.map((ndc, idx) => {
                    const checked = selectedNDCsForEdit.includes(ndc.NDC11);
                    return (
                      <label
                        key={ndc.NDC11}
                        htmlFor={`v2-ndc-${ndc.NDC11}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          padding: '0.6rem 0.85rem',
                          borderTop:
                            idx === 0 ? 'none' : '1px solid var(--border-primary)',
                          cursor: 'pointer',
                        }}
                      >
                        <Checkbox
                          inputId={`v2-ndc-${ndc.NDC11}`}
                          checked={checked}
                          onChange={() => toggleNDCSelection(ndc.NDC11)}
                        />
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span style={{ fontWeight: 600, fontSize: 13 }}>
                            {ndc.NDC11}
                          </span>
                          <span style={{ fontSize: 11, color: '#6b7280' }}>
                            {ndc.Concept}
                          </span>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {temporaryNDCs.length > 0 && (
              <div>
                <h4
                  style={{
                    margin: '0 0 8px',
                    fontSize: 12,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    color: '#374151',
                    letterSpacing: 0.4,
                  }}
                >
                  Temporary Packout NDCs
                </h4>
                <div
                  style={{
                    border: '1px solid var(--border-primary)',
                    borderRadius: 8,
                    background: 'var(--bg-card)',
                  }}
                >
                  {temporaryNDCs.map((ndc, idx) => {
                    const checked = selectedNDCsForEdit.includes(ndc.TempNDC);
                    return (
                      <label
                        key={ndc.ID}
                        htmlFor={`v2-temp-${ndc.ID}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          padding: '0.6rem 0.85rem',
                          borderTop:
                            idx === 0 ? 'none' : '1px solid var(--border-primary)',
                          cursor: 'pointer',
                        }}
                      >
                        <Checkbox
                          inputId={`v2-temp-${ndc.ID}`}
                          checked={checked}
                          onChange={() => toggleNDCSelection(ndc.TempNDC)}
                        />
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span style={{ fontWeight: 600, fontSize: 13 }}>
                            {ndc.TempNDC}
                          </span>
                          <span style={{ fontSize: 11, color: '#6b7280' }}>
                            {ndc.Type}
                          </span>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {!isLoadingNDCs && !hasAnyNDCs && (
          <CreateTempNDCInline
            pendingTempType={pendingTempType}
            setPendingTempType={setPendingTempType}
            isCreating={isCreatingTempNDC}
            onCreate={handleCreateTempNDC}
          />
        )}

        <div style={{ flexGrow: 1 }} />

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '0.5rem',
            paddingTop: '0.75rem',
            borderTop: '1px solid var(--border-primary)',
          }}
        >
          <Button label="Cancel" outlined onClick={closeSidebar} />
          <Button
            label="Continue"
            disabled={!canContinue}
            onClick={handleContinue}
          />
        </div>
      </div>
    </RightPanel>
  );
});

interface CreateTempNDCInlineProps {
  pendingTempType: TemporaryPackoutNDCType | null;
  setPendingTempType: (t: TemporaryPackoutNDCType | null) => void;
  isCreating: boolean;
  onCreate: () => void;
}

const CreateTempNDCInline = ({
  pendingTempType,
  setPendingTempType,
  isCreating,
  onCreate,
}: CreateTempNDCInlineProps) => {
  const renderCard = (
    value: TemporaryPackoutNDCType,
    title: string,
    subtitle: string
  ) => {
    const isOn = pendingTempType === value;
    return (
      <button
        type="button"
        onClick={() => setPendingTempType(value)}
        style={{
          flex: 1,
          textAlign: 'left',
          padding: '0.75rem 0.9rem',
          borderRadius: 10,
          border: `1.5px solid ${isOn ? '#0e7490' : 'var(--border-primary)'}`,
          background: isOn ? '#ecfeff' : 'var(--bg-card)',
          cursor: 'pointer',
          transition: 'background 0.15s, border-color 0.15s',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        <span
          style={{
            fontWeight: 700,
            fontSize: 13,
            color: isOn ? '#0e7490' : 'var(--text-primary)',
          }}
        >
          {title}
        </span>
        <span style={{ fontSize: 11, color: '#6b7280', lineHeight: 1.35 }}>
          {subtitle}
        </span>
      </button>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
      <div
        style={{
          padding: '0.85rem 1rem',
          borderRadius: 10,
          background: '#fff7ed',
          border: '1px solid #fed7aa',
          color: '#9a3412',
          fontSize: 12.5,
          lineHeight: 1.5,
        }}
      >
        <strong style={{ display: 'block', marginBottom: 4 }}>
          No NDCs yet
        </strong>
        This concept has no active real NDCs and no active temporary
        packout NDCs. Create a temporary packout NDC below to begin
        forecasting.
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
        <label
          style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}
        >
          Forecast category
        </label>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {renderCard(
            'PackoutMAS',
            'Packout MAS',
            'Multiple-Award Schedule contract.'
          )}
          {renderCard(
            'PackoutNC',
            'Packout NC',
            'National Contract (VA / DoD).'
          )}
        </div>
      </div>

      <Button
        label={isCreating ? 'Creating…' : 'Create Temporary NDC'}
        disabled={!pendingTempType || isCreating}
        loading={isCreating}
        onClick={onCreate}
      />
    </div>
  );
};

/**
 * Loading-state placeholder for the sidebar's NDC list. Renders a
 * section heading skeleton plus three NDC-row skeletons that match
 * the visual weight of the eventual real list (checkbox + two-line
 * label per row). Gives the user a sense of the panel's eventual
 * size instead of the previous tiny "Loading…" text.
 */
const NDCListSkeleton = () => {
  const rowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.6rem 0.85rem',
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <Skeleton width="9rem" height="0.85rem" borderRadius="4px" />
      <div
        style={{
          border: '1px solid var(--border-primary)',
          borderRadius: 8,
          background: 'var(--bg-card)',
        }}
      >
        {[0, 1, 2].map((i, idx) => (
          <div
            key={i}
            style={{
              ...rowStyle,
              borderTop: idx === 0 ? 'none' : '1px solid var(--border-primary)',
            }}
          >
            <Skeleton width="1.25rem" height="1.25rem" borderRadius="4px" />
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                flex: 1,
              }}
            >
              <Skeleton width="60%" height="0.85rem" borderRadius="4px" />
              <Skeleton width="85%" height="0.7rem" borderRadius="4px" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ConceptDetailSidebar;
